/**
 * Indian-mobile shape check and E.164 normalisation, without libphonenumber.
 *
 * ── Why not `@interioring/utils/validation/phone` ────────────────────────
 * That module is the API's normaliser and is correct for what it does — it
 * classifies landlines, parses international numbers, and produces the
 * canonical `phone_norm` the database dedupes on. It is also expensive: the
 * barrel re-exports `./zod`, so importing two functions from it pulls
 * libphonenumber-js (30.0 KB gzipped, most of it world phone metadata) AND
 * zod (12.4 KB) into the initial chunk — 35 KB, 29% of this app's entire JS
 * budget, to check ten digits typed on a phone.
 *
 * NFR-2 caps initial JS at 120 KB gzipped for a mid-range Android on 4G. With
 * that import the app measured 146.6 KB; without it, 104.6 KB.
 *
 * `apps/go/src/lib/phone.ts` already made this exact trade for the same form,
 * and its header states the principle this file inherits: the API remains the
 * single source of truth for what is stored, and this "never has to agree with
 * it byte for byte, only reject the same shapes a person would already
 * recognise as wrong". The partner login is a stronger case still — the API
 * deliberately does not validate the format at all (`requestSchema` in
 * routes/partner/auth.routes.ts takes any 1–32 char string, so a typo and an
 * unknown number get the identical `{ok:true}`), which makes this check purely
 * a courtesy that saves a partner one wasted round trip.
 *
 * Equivalence with libphonenumber over the inputs this app can produce is not
 * asserted, it is TESTED: phone.test.ts runs both implementations over the
 * same case list and fails on any disagreement.
 */

/** Removes the punctuation a person actually types: spaces, dashes, brackets. */
function stripPunctuation(input: string): string {
	return input.replace(/[\s\-()]/g, "");
}

/**
 * Digits of an Indian mobile, or null.
 *
 * Strips an optional `+91`/`91` country code and an optional trunk `0`, in
 * that order — `+91 09876543210` is a real thing people write, and it is a
 * country code followed by a trunk prefix, not a 11-digit subscriber number.
 *
 * A prefix is only removed when doing so leaves exactly ten digits. Without
 * that guard a genuine 10-digit mobile beginning "91" would be silently
 * truncated to eight.
 */
function toTenDigits(input: string | null | undefined): string | null {
	if (!input) return null;
	const stripped = stripPunctuation(input.trim());
	if (!stripped || !/^\+?\d+$/.test(stripped)) return null;

	let digits = stripped.startsWith("+") ? stripped.slice(1) : stripped;
	if (
		digits.startsWith("91") &&
		(digits.length === 12 || digits.length === 13)
	) {
		digits = digits.slice(2);
	}
	if (digits.startsWith("0") && digits.length === 11) {
		digits = digits.slice(1);
	}

	return /^[6-9]\d{9}$/.test(digits) ? digits : null;
}

/** True when the input is a well-formed Indian mobile number. */
export function isValidIndianMobile(input: string | null | undefined): boolean {
	return toTenDigits(input) !== null;
}

/** `+91XXXXXXXXXX`, or null when the input is not an Indian mobile. */
export function normalizeToE164(
	input: string | null | undefined,
): string | null {
	const digits = toTenDigits(input);
	return digits ? `+91${digits}` : null;
}
